import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Image } from 'lucide-react';

const getStatusBadge = (status) => {
  const styles = { disponible: 'bg-green-100 text-green-800', vendu: 'bg-gray-100 text-gray-800' };
  return styles[status] || 'bg-yellow-100 text-yellow-800';
};

export function VehiclesTab({ vehicles, onEdit, onMarkSold, onMarkAvailable, onDelete, onShowForm, VehicleForm, showVehicleForm, editingVehicle, onVehicleSave, onVehicleCancel, authHeaders }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={onShowForm} className="cta-button" data-testid="add-vehicle-btn">
          + Ajouter un véhicule
        </button>
      </div>

      {showVehicleForm && (
        <VehicleForm
          vehicle={editingVehicle}
          onSave={onVehicleSave}
          onCancel={onVehicleCancel}
          authHeaders={authHeaders}
        />
      )}

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="data-table" data-testid="vehicles-table">
          <thead>
            <tr><th>Véhicule</th><th>Prix</th><th>Année</th><th>Km</th><th>Statut</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {vehicles.map(vehicle => (
              <tr key={vehicle.id}>
                <td>
                  <div className="flex items-center gap-3">
                    <img src={vehicle.images?.[0] || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=100'} alt="" className="w-12 h-12 object-cover rounded" />
                    <div>
                      <p className="font-medium text-gray-900">{vehicle.title}</p>
                      <p className="text-sm text-gray-500">{vehicle.brand} {vehicle.model}</p>
                    </div>
                  </div>
                </td>
                <td className="font-semibold">{vehicle.price.toLocaleString('fr-FR')} €</td>
                <td>{vehicle.year}</td>
                <td>{vehicle.mileage.toLocaleString('fr-FR')} km</td>
                <td><span className={`badge ${getStatusBadge(vehicle.status)}`}>{vehicle.status}</span></td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => onEdit(vehicle)} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700" data-testid={`edit-vehicle-${vehicle.id}`}>Modifier</button>
                    {vehicle.status !== 'vendu' ? (
                      <button onClick={() => onMarkSold(vehicle)} className="px-3 py-1 bg-orange-500 text-white text-sm rounded hover:bg-orange-600" data-testid={`sold-vehicle-${vehicle.id}`}>Vendu</button>
                    ) : (
                      <button onClick={() => onMarkAvailable(vehicle)} className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700" data-testid={`available-vehicle-${vehicle.id}`}>Remettre</button>
                    )}
                    <button onClick={() => onDelete(vehicle.id)} className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700" data-testid={`delete-vehicle-${vehicle.id}`}>Supprimer</button>
                  </div>
                </td>
              </tr>
            ))}
            {vehicles.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-gray-500">Aucun véhicule enregistré</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {vehicles.map(vehicle => (
          <div key={vehicle.id} className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="flex">
              <img src={vehicle.images?.[0] || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=100'} alt="" className="w-28 h-28 object-cover" />
              <div className="flex-1 p-3">
                <h3 className="font-bold text-gray-900 text-sm">{vehicle.title}</h3>
                <p className="text-[#DC2626] font-bold text-lg">{vehicle.price.toLocaleString('fr-FR')} €</p>
                <div className="flex gap-2 text-xs text-gray-500 mt-1">
                  <span>{vehicle.year}</span><span>•</span><span>{vehicle.mileage.toLocaleString('fr-FR')} km</span><span>•</span><span>{vehicle.fuel}</span>
                </div>
                <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded ${getStatusBadge(vehicle.status)}`}>{vehicle.status}</span>
              </div>
            </div>
            <div className="flex border-t border-gray-200">
              <button onClick={() => onEdit(vehicle)} className="flex-1 py-3 text-center text-blue-600 font-medium text-sm hover:bg-blue-50 border-r border-gray-200" data-testid={`edit-vehicle-mobile-${vehicle.id}`}>Modifier</button>
              {vehicle.status !== 'vendu' ? (
                <button onClick={() => onMarkSold(vehicle)} className="flex-1 py-3 text-center text-orange-600 font-medium text-sm hover:bg-orange-50 border-r border-gray-200" data-testid={`sold-vehicle-mobile-${vehicle.id}`}>Vendu</button>
              ) : (
                <button onClick={() => onMarkAvailable(vehicle)} className="flex-1 py-3 text-center text-green-600 font-medium text-sm hover:bg-green-50 border-r border-gray-200" data-testid={`available-vehicle-mobile-${vehicle.id}`}>Remettre</button>
              )}
              <button onClick={() => onDelete(vehicle.id)} className="flex-1 py-3 text-center text-red-600 font-medium text-sm hover:bg-red-50" data-testid={`delete-vehicle-mobile-${vehicle.id}`}>Supprimer</button>
            </div>
          </div>
        ))}
        {vehicles.length === 0 && <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-gray-500">Aucun véhicule enregistré</div>}
      </div>
    </div>
  );
}

export function ContactsTab({ contacts, onMarkRead, onDelete }) {
  return (
    <div className="space-y-4">
      {contacts.map(contact => (
        <div key={contact.id} className={`bg-white border rounded-md p-6 ${!contact.read ? 'border-[#DC2626]' : 'border-gray-200'}`} data-testid={`contact-${contact.id}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-gray-900">{contact.name}</h3>
                {!contact.read && <span className="bg-[#DC2626] text-white text-xs px-2 py-0.5 rounded">Nouveau</span>}
              </div>
              <p className="text-sm text-gray-500">{contact.email} • {contact.phone}</p>
            </div>
            <div className="text-sm text-gray-500">
              {new Date(contact.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <p className="font-medium text-gray-900 mb-2">{contact.subject}</p>
          <p className="text-gray-600 mb-4 whitespace-pre-line">{contact.message}</p>
          {contact.vehicle_id && (
            <p className="text-sm text-[#DC2626] mb-4">Concerne un véhicule: <Link to={`/vehicules/${contact.vehicle_id}`} className="underline">{contact.vehicle_id}</Link></p>
          )}
          <div className="flex gap-3">
            {!contact.read && <button onClick={() => onMarkRead(contact.id)} className="text-sm text-gray-600 hover:underline">Marquer comme lu</button>}
            <a href={`mailto:${contact.email}`} className="text-sm text-blue-600 hover:underline">Répondre par email</a>
            <a href={`tel:${contact.phone}`} className="text-sm text-green-600 hover:underline">Appeler</a>
            <button onClick={() => onDelete(contact.id)} className="text-sm text-red-600 hover:underline">Supprimer</button>
          </div>
        </div>
      ))}
      {contacts.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-md p-12 text-center">
          <MessageSquare className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-500">Aucun message reçu</p>
        </div>
      )}
    </div>
  );
}

export function PromotionsTab({ promotions, onEdit, onDelete, onShowForm, PromotionForm, showPromoForm, editingPromo, onPromoSave, onPromoCancel, authHeaders }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button onClick={onShowForm} className="cta-button" data-testid="add-promo-btn">
          + Ajouter une promotion
        </button>
      </div>

      {showPromoForm && (
        <PromotionForm
          promotion={editingPromo}
          onSave={onPromoSave}
          onCancel={onPromoCancel}
          authHeaders={authHeaders}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {promotions.map(promo => (
          <div key={promo.id} className={`bg-white border rounded-md overflow-hidden ${promo.active ? 'border-green-300' : 'border-gray-200 opacity-60'}`} data-testid={`promo-${promo.id}`}>
            <div className="relative h-40">
              <img src={promo.image_url} alt={promo.title} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-2">
                <span className={`text-xs font-bold px-2 py-1 rounded ${promo.active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                  {promo.active ? 'Active' : 'Inactive'}
                </span>
                <span className="bg-black/70 text-white text-xs px-2 py-1 rounded">Ordre: {promo.order}</span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-gray-900 mb-1">{promo.title}</h3>
              {promo.description && <p className="text-sm text-gray-600 mb-3">{promo.description}</p>}
              <div className="flex gap-2">
                <button onClick={() => onEdit(promo)} className="text-sm text-blue-600 hover:underline" data-testid={`edit-promo-${promo.id}`}>Modifier</button>
                <button onClick={() => onDelete(promo.id)} className="text-sm text-red-600 hover:underline" data-testid={`delete-promo-${promo.id}`}>Supprimer</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {promotions.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-md p-12 text-center">
          <Image className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-500 mb-4">Aucune promotion créée</p>
          <button onClick={onShowForm} className="text-[#DC2626] font-semibold hover:underline">
            Créer votre première promotion
          </button>
        </div>
      )}
    </div>
  );
}
